// src/i18n.js
//
// Pass-9 follow-up: migrated from gt-react (cloud-runtime, needed a paid
// API quota we never funded — Spanish never actually translated on the
// live site) to react-i18next backed by the local JSON dictionaries
// already committed under src/translations/.
//
// Contract:
//   * The English source string is the LOOKUP KEY. translations/es.json
//     etc. map English source → translated string. This matches how the
//     existing `<T>Some English string</T>` calls flow: the children's
//     text content becomes the key.
//   * `keySeparator: false` + `nsSeparator: false` so colons and dots
//     inside source strings are not interpreted as namespaces.
//   * Missing translations fall back to the source string (the key).
//     Better than rendering "[missing translation]" — the page still
//     reads in English while the dictionary catches up.
//   * Locale persists to localStorage under `518cares-locale-v1`, the
//     same key the pass-7 LanguagePicker + pass-9 LanguageBanner already
//     write to. Cross-component invariant preserved.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Eagerly load every dictionary under ./translations. Adding a new locale
// is now just dropping a `<code>.json` file in that folder — it registers
// itself here and shows up in the LanguagePicker automatically, matching
// the gt.config.json `locales` list.
const dictionaries = import.meta.glob('./translations/*.json', { eager: true });

const resources = {};
const loadedCodes = [];
for (const [path, mod] of Object.entries(dictionaries)) {
  const code = path.slice(path.lastIndexOf('/') + 1, -'.json'.length);
  resources[code] = { translation: mod.default || mod };
  loadedCodes.push(code);
}

export const LOCALE_STORAGE_KEY = '518cares-locale-v1';
// Every locale we ship a dictionary for — 'en' first, the rest alphabetical.
export const SUPPORTED_LOCALES = [
  'en',
  ...loadedCodes.filter((c) => c !== 'en').sort(),
];

const initialLocale = (() => {
  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
  } catch {
    /* private mode / SSR */
  }
  return 'en';
})();

i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale,
  fallbackLng: 'en',
  // Use the English source string as the key. The existing translation
  // files already follow this shape (e.g. "About": "Acerca de").
  keySeparator: false,
  nsSeparator: false,
  // When a key isn't in the dictionary, return the key itself — that
  // gives English fallback in production while translations are being
  // filled in, instead of an obvious "[missing]" hole.
  returnEmptyString: false,
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    // Wrap rendered output in a Fragment, not a <span>. Keeps existing
    // DOM structure identical so CSS selectors that target the parent
    // still match.
    transWrapTextNodes: '',
  },
});

// Right-to-left scripts need the whole document mirrored, not just text
// alignment — flip <html dir> (and keep lang in sync for a11y/SEO).
const RTL_LOCALES = new Set(['ar', 'he', 'fa']);
function applyDocumentLocale(lng) {
  try {
    document.documentElement.lang = lng;
    document.documentElement.dir = RTL_LOCALES.has(lng) ? 'rtl' : 'ltr';
  } catch {
    /* SSR / non-DOM env */
  }
}
applyDocumentLocale(initialLocale);

// Persist the new locale every time it changes so a reload restores it,
// and keep the document's lang/dir in sync with the active locale.
i18n.on('languageChanged', (lng) => {
  applyDocumentLocale(lng);
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, lng);
  } catch {
    /* noop */
  }
});

export default i18n;
